'use client';

import React, { createContext, useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

type ToastOptions = { duration?: number };
type ToastItem = { id: number; message: string; visible?: boolean; };

type ToastContextValue = {
    // Push a toast message to the screen
    toast: (message: string, opts?: ToastOptions) => void
};

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function Toast({ children, defaultDuration = 3000,  }: { children: React.ReactNode, defaultDuration?: number; }) {
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);

    const [items, setItems] = useState<ToastItem[]>([]);
    const idRef = useRef(1);

    const toast = useCallback((message: string, opts?: ToastOptions) => {
        const id = idRef.current++;
        const duration = opts?.duration ?? defaultDuration;

        // Add invisible initially
        setItems(prev => [...prev, { id, message, visible: false }]);

        // Trigger fade-in on next frame
        requestAnimationFrame(() => {
            setItems(prev => prev.map(t => (t.id === id ? { ...t, visible: true } : t)));
        });

        // Schedule fade-out before removal
        const fadeMs = 300;
        setTimeout(() => {
            setItems(prev => prev.map(t => (t.id === id ? { ...t, visible: false } : t)));
        }, Math.max(0, duration - fadeMs));

        // Remove toast
        setTimeout(() => {
            setItems(prev => prev.filter(t => t.id !== id));
        }, duration);
    }, [defaultDuration]);

    const ctx = useMemo(() => ({ toast }), [toast]);

    if (!mounted) {
        return <ToastContext.Provider value={ctx}>{children}</ToastContext.Provider>;
    }

    const portal = createPortal(
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[99999] flex flex-col items-center gap-3 pointer-events-none">
            {items.map(t => (
                <div
                    key={t.id}
                    className={`
                        backdrop-blur-md bg-white/30 border border-white/40 shadow-lg
                        rounded-xl px-6 py-3 text-sm font-medium text-gray-800
                        transition-all duration-300 ease-out transform
                        ${t.visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 -translate-y-4 scale-95'}
                    `}
                >
                    {t.message}
                </div>
            ))}
        </div>,
        document.body
    );

    return (
        <ToastContext.Provider value={ctx}>
            {children}
            {portal}
        </ToastContext.Provider>
    );
}

export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToast must be used within <Toast>');
    return ctx;
}
